import React, { useEffect, useState } from "react";
import usePipelinesApi, { CardLossReasonItem } from "../Pipeline/use-pipelines";
import { useToast } from "../..";
import { Container, ListLossReasons } from "./styles";
import { Button } from "../../";
import { AuthContextType, useAuth } from "../../../auth/auth-provider";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { v } from "../../../util";

type LossReasonProps = {
  lossReasons: CardLossReasonItem[];
  onClose: () => void;
  onSuccess: () => void;
};

type DraftLossReasonItem = {
  lossReasonId?: number;
  tempId: string;
  reason: string;
  isNew: boolean;
};

type LossReasonFormValues = {
  lossReasons: DraftLossReasonItem[];
};

const updateLossReasonSchema = v.object({
  lossReasons: v.array(
    v.object({
      lossReasonId: v.number().optional(),
      tempId: v.string(),
      reason: v.string().trim().superRefine(v.nonempty()),
      isNew: v.boolean()
    })
  )
});

const LossReason: React.FC<LossReasonProps> = ({
  lossReasons,
  onClose,
  onSuccess
}) => {
  const toast = useToast();
  const { createCardLossReason, editCardLossReason, deleteCardLossReason } =
    usePipelinesApi();
  const { user, company, hasAnyPermission } = useAuth() as AuthContextType;
  const companyId = user.admin ? company?.id : user.companyId;

  const [initialLossReasons, setInitialLossReasons] = useState<
    CardLossReasonItem[]
  >([]);
  const [confirmCloseModal, setConfirmCloseModal] = useState(false);
  const [lossReasonIdToDelete, setLossReasonIdToDelete] = useState<
    number | null
  >(null);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors }
  } = useForm<LossReasonFormValues>({
    defaultValues: {
      lossReasons: []
    },
    resolver: zodResolver(updateLossReasonSchema),
    shouldFocusError: true
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "lossReasons"
  });

  useEffect(() => {
    const nextLossReasons = lossReasons ?? [];
    setInitialLossReasons(nextLossReasons);
    reset({
      lossReasons: nextLossReasons.map((item) => ({
        lossReasonId: item.id,
        tempId: String(item.id),
        reason: item.reason ?? "",
        isNew: false
      }))
    });
  }, [lossReasons, reset]);

  const startCreateLossReason = () => {
    append({
      tempId: `${Date.now()}-${Math.random()}`,
      reason: "",
      isNew: true
    });
  };

  const handleDeleteLossReason = async () => {
    if (!lossReasonIdToDelete) return;

    try {
      await deleteCardLossReason(lossReasonIdToDelete);

      const currentLossReasons = getValues("lossReasons");
      const updatedLossReasons = currentLossReasons.filter(
        (item) => item.lossReasonId !== lossReasonIdToDelete
      );

      replace(updatedLossReasons);
      setInitialLossReasons((prev) =>
        prev.filter((item) => item.id !== lossReasonIdToDelete)
      );

      setConfirmCloseModal(false);
      setLossReasonIdToDelete(null);

      toast?.current?.show({
        life: 3000,
        severity: "success",
        summary: "Sucesso!",
        detail: "Motivo de não conversão excluído com sucesso!"
      });

      onSuccess();
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Erro ao excluir motivo de não conversão.";

      setConfirmCloseModal(false);
      setLossReasonIdToDelete(null);

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  };

  const hasLossReasonChanges = (currentLossReasons: DraftLossReasonItem[]) => {
    if (currentLossReasons.length !== initialLossReasons.length) {
      return true;
    }

    const originalLossReasonsSorted = [...initialLossReasons]
      .map((item) => ({
        lossReasonId: item.id,
        reason: item.reason ?? ""
      }))
      .sort((a, b) => (a.lossReasonId ?? 0) - (b.lossReasonId ?? 0));

    const currentLossReasonsSorted = [...currentLossReasons]
      .map((item) => ({
        lossReasonId: item.lossReasonId,
        reason: item.reason.trim()
      }))
      .sort((a, b) => (a.lossReasonId ?? 0) - (b.lossReasonId ?? 0));

    return (
      JSON.stringify(originalLossReasonsSorted) !==
      JSON.stringify(currentLossReasonsSorted)
    );
  };

  const handleSaveAll = handleSubmit(async (data) => {
    try {
      const canEditLossReasons =
        user.admin || hasAnyPermission("contactPipeline_edit");

      if (!canEditLossReasons) {
        toast?.current?.show({
          life: 5000,
          severity: "warn",
          summary: "Aviso",
          detail: "Você não tem permissão para editar motivos de não conversão."
        });
        return;
      }

      if (!companyId) {
        toast?.current?.show({
          life: 5000,
          severity: "error",
          summary: "Erro",
          detail: "Empresa não encontrada para salvar motivos de não conversão."
        });
        return;
      }

      if (!hasLossReasonChanges(data.lossReasons)) {
        onClose();
        return;
      }

      const existingMap = new Map(
        initialLossReasons.map((item) => [item.id, item.reason?.trim() ?? ""])
      );

      for (const item of data.lossReasons) {
        const trimmedReason = item.reason.trim();

        if (!item.lossReasonId) {
          await createCardLossReason({
            companyId,
            reason: trimmedReason
          });
          continue;
        }

        const originalReason = existingMap.get(item.lossReasonId) ?? "";
        if (originalReason !== trimmedReason) {
          await editCardLossReason({
            id: item.lossReasonId,
            reason: trimmedReason
          });
        }
      }

      toast?.current?.show({
        life: 3000,
        severity: "success",
        summary: "Sucesso!",
        detail: "Motivos de não conversão salvos com sucesso!"
      });

      onSuccess();
      onClose();
    } catch (e: any) {
      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail:
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Erro ao salvar motivos de não conversão."
      });
    }
  });

  return (
    <Container>
      <ListLossReasons>
        <div className="helperText">
          Cadastre os motivos de não conversão disponíveis para a empresa
        </div>

        <div className="divisor">
          <div className="listLossReasons">
            {fields.map((item, index) => (
              <div key={item.tempId} className="lossReasonRow">
                <div className="lossReasonInputWrapper">
                  <Controller
                    name={`lossReasons.${index}.reason`}
                    control={control}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        value={field.value ?? ""}
                        placeholder="Nome do motivo de não conversão"
                        className="inlineInput"
                      />
                    )}
                  />

                  {errors.lossReasons?.[index]?.reason?.message ? (
                    <div className="inlineErrors">
                      {errors.lossReasons[index]?.reason?.message}
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  className="deleteButton"
                  onClick={(e) => {
                    e.stopPropagation();

                    if (item.isNew) {
                      remove(index);
                      return;
                    }

                    setLossReasonIdToDelete(item.lossReasonId ?? null);
                    setConfirmCloseModal(true);
                  }}
                >
                  <i className="pi pi-trash" />
                </button>
              </div>
            ))}

            <div
              className="addLossReasonButton"
              onClick={startCreateLossReason}
            >
              <i className="pi pi-plus" style={{ fontSize: "14px" }} />{" "}
              Adicionar motivo de não conversão
            </div>
          </div>
        </div>

        <div className="buttons">
          <Button
            label="CANCELAR"
            className="no-hover-button"
            onClick={onClose}
          />
          <Button
            label="SALVAR"
            onClick={handleSaveAll}
            style={{
              background: user.plataform.mainColor
                ? user.plataform.mainColor
                : "#2624CD",
              borderColor: user.plataform.mainColor
                ? user.plataform.mainColor
                : "#2624CD",
              color: "#fff"
            }}
          />
        </div>
      </ListLossReasons>

      <Dialog
        visible={confirmCloseModal}
        onHide={() => setConfirmCloseModal(false)}
        draggable={false}
        header="Deseja excluir o motivo de não conversão?"
        style={{ width: "575px" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            Ao excluir o motivo de não conversão, a relação dele com todos os
            cards será perdida e essa ação não poderá ser desfeita. Tem certeza
            que deseja excluir?
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "end",
              gap: "15px"
            }}
          >
            <Button
              label="Sim"
              className="p-button-danger"
              onClick={handleDeleteLossReason}
            />
            <Button
              label="Não"
              className="p-button-secondary"
              onClick={() => {
                setConfirmCloseModal(false);
                setLossReasonIdToDelete(null);
              }}
            />
          </div>
        </div>
      </Dialog>
    </Container>
  );
};

export default LossReason;
